import axios from 'axios';

// Use relative URL in production (works with Caddy), absolute URL in development
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:5000/api');

class TranscriptionService {
  /**
   * Request a presigned URL for uploading an audio file
   * @param {File} file - The audio file to upload
   * @returns {Promise<{uploadId: string, presignedUrl: string, objectName: string}>}
   */
  async requestPresignedUrl(file) {
    const response = await axios.post(
      `${API_BASE_URL}/transcription/presigned-url`,
      {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'audio/mpeg',
      }
    );
    return response.data;
  }

  /**
   * Upload audio file directly to Oracle Cloud using presigned URL
   * @param {File} file - The audio file to upload
   * @param {Function} onProgress - Progress callback (progress: {percent: number, loaded: number, total: number}) => void
   * @returns {Promise<{objectName: string}>}
   */
  async uploadAudio(file, onProgress) {
    try {
      // Step 1: Request presigned URL
      const { presignedUrl, objectName } = await this.requestPresignedUrl(file);

      // Step 2: Upload directly to Oracle Cloud using presigned URL
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type || 'audio/mpeg',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );

            onProgress({
              percent: percentCompleted,
              loaded: progressEvent.loaded,
              total: progressEvent.total,
            });
          }
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      return { objectName };
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload audio');
    }
  }

  /**
   * Transcribe audio file
   * @param {string} objectName - The object name of the uploaded audio file
   * @returns {Promise<{ass: string, objectName: string}>}
   */
  async transcribeAudio(objectName) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/transcription/transcribe`,
        {
          objectName: objectName,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw new Error(
        error.response?.data?.error || 'Failed to transcribe audio'
      );
    }
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

export default new TranscriptionService();





